<template>
  <div class="books-container">
    <el-container>
      <!-- 头部导航 -->
      <el-header>
        <div class="header-content">
          <div class="header-left">
            <h1>TXT 在线阅读平台</h1>
          </div>
          <div class="header-right">
            <el-dropdown @command="handleCommand">
              <span class="el-dropdown-link">
                {{ currentUser.username }}
                <el-icon><arrow-down /></el-icon>
              </span>
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item v-if="isAdmin" command="adminUsers">用户管理</el-dropdown-item>
                  <el-dropdown-item v-if="isAdmin" command="adminBooks">书籍管理</el-dropdown-item>
                  <el-dropdown-item divided command="logout">退出登录</el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
          </div>
        </div>
      </el-header>

      <!-- 主要内容 -->
      <el-main>
        <div class="books-header">
          <h2>书籍列表</h2>
          <el-button v-if="isAdmin" type="primary" @click="showUploadDialog = true">
            <el-icon><upload /></el-icon>
            上传书籍
          </el-button>
        </div>

        <!-- 书籍列表 -->
        <el-row :gutter="20">
          <el-col
            v-for="book in books"
            :key="book.id"
            :xs="24"
            :sm="12"
            :md="8"
            :lg="6"
            :xl="4"
          >
            <el-card class="book-card" shadow="hover" @click="openBook(book.id)">
              <template #header>
                <div class="book-header">
                  <h3>{{ book.title }}</h3>
                  <el-tag v-if="book.author" size="small">{{ book.author }}</el-tag>
                </div>
              </template>
              <div class="book-content">
                <p v-if="book.description" class="book-description">{{ book.description }}</p>
                <p v-else class="book-no-description">暂无描述</p>
                <div class="book-meta">
                  <span><el-icon><document /></el-icon> {{ formatFileSize(book.file_size) }}</span>
                  <span><el-icon><reading /></el-icon> {{ book.total_chars || 0 }} 字</span>
                </div>
                <!-- 阅读进度 -->
                <div class="reading-progress">
                  <el-progress
                    :percentage="book.progress_percentage || 0"
                    :stroke-width="6"
                    :show-text="false"
                    :color="getProgressColor(book.progress_percentage)"
                  />
                  <span class="progress-text">{{ book.progress_percentage || 0 }}%</span>
                </div>
              </div>
            </el-card>
          </el-col>
        </el-row>

        <!-- 分页 -->
        <el-pagination
          v-if="total > 0"
          v-model:current-page="currentPage"
          v-model:page-size="pageSize"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />

        <!-- 空状态 -->
        <el-empty v-if="books.length === 0 && !loading" description="暂无书籍">
          <el-button v-if="isAdmin" type="primary" @click="showUploadDialog = true">
            上传第一本书籍
          </el-button>
        </el-empty>

        <!-- 加载状态 -->
        <div v-if="loading" class="loading-container">
          <el-icon class="loading-icon"><loading /></el-icon>
          <p>加载中...</p>
        </div>
      </el-main>
    </el-container>

    <!-- 上传书籍对话框 -->
    <el-dialog v-model="showUploadDialog" title="上传书籍" width="500px">
      <el-form ref="uploadForm" :model="uploadForm" :rules="uploadRules" label-width="80px">
        <el-form-item label="书名" prop="title">
          <el-input v-model="uploadForm.title" placeholder="请输入书名" />
        </el-form-item>
        <el-form-item label="作者" prop="author">
          <el-input v-model="uploadForm.author" placeholder="请输入作者" />
        </el-form-item>
        <el-form-item label="描述" prop="description">
          <el-input
            v-model="uploadForm.description"
            type="textarea"
            :rows="3"
            placeholder="请输入书籍描述"
          />
        </el-form-item>
        <el-form-item label="文件" prop="file">
          <el-upload
            class="upload-demo"
            drag
            action="#"
            :auto-upload="false"
            :on-change="handleFileChange"
            :before-upload="() => false"
            accept=".txt"
          >
            <el-icon class="el-icon--upload"><upload-filled /></el-icon>
            <div class="el-upload__text">
              将 TXT 文件拖到此处，或<em>点击上传</em>
            </div>
            <template #tip>
              <div class="el-upload__tip">
                只能上传 TXT 文件，且不超过 10MB
              </div>
            </template>
          </el-upload>
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="showUploadDialog = false">取消</el-button>
          <el-button type="primary" :loading="uploading" @click="handleUpload">
            上传
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import api from '../api'

export default {
  name: 'Books',
  data() {
    return {
      books: [],
      loading: false,
      currentPage: 1,
      pageSize: 10,
      total: 0,
      showUploadDialog: false,
      uploading: false,
      uploadForm: {
        title: '',
        author: '',
        description: '',
        file: null
      },
      uploadRules: {
        title: [{ required: true, message: '请输入书名', trigger: 'blur' }],
        file: [{ required: true, message: '请选择文件', trigger: 'change' }]
      }
    }
  },
  computed: {
    ...mapGetters(['currentUser', 'isAdmin'])
  },
  mounted() {
    this.fetchBooks()
  },
  methods: {
    ...mapActions(['logout']),
    async fetchBooks() {
      this.loading = true
      try {
        const params = {
          skip: (this.currentPage - 1) * this.pageSize,
          limit: this.pageSize
        }
        const data = await api.getBooks(params)
        this.books = data
        // 注意：实际 API 应返回总数，这里简化处理
        this.total = data.length
        
        // 加载每本书的阅读进度
        this.loadReadingProgresses()
      } catch (error) {
        console.error('获取书籍列表失败:', error)
        this.$message.error('获取书籍列表失败')
      } finally {
        this.loading = false
      }
    },
    async loadReadingProgresses() {
      // 并行加载所有书籍的进度
      const progressPromises = this.books.map(async (book) => {
        try {
          const progress = await api.getReadingProgress(book.id)
          book.progress_percentage = progress.progress_percentage || 0
          book.page = progress.page || 1  // 保存页码
        } catch (error) {
          // 无进度记录时使用 0
          book.progress_percentage = 0
          book.page = 1
        }
      })
      await Promise.all(progressPromises)
    },
    getProgressColor(percentage) {
      if (percentage >= 100) return '#67c23a'
      if (percentage >= 50) return '#409eff'
      if (percentage >= 20) return '#e6a23c'
      return '#f56c6c'
    },
    formatFileSize(bytes) {
      if (!bytes) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },
    openBook(bookId) {
      const userId = this.currentUser.id
      // 获取这本书的阅读进度页码
      const book = this.books.find(b => b.id === bookId)
      const page = book ? (book.page || 1) : 1
      console.log('openBook:', { bookId, userId, page, book })
      this.$router.push({
        name: 'Reader',
        params: { bookId },
        query: { userId, page }
      })
    },
    handleCommand(command) {
      switch (command) {
        case 'adminUsers':
          this.$router.push('/admin/users')
          break
        case 'adminBooks':
          this.$router.push('/admin/books')
          break
        case 'logout':
          this.handleLogout()
          break
      }
    },
    handleLogout() {
      this.logout()
      this.$router.push('/login')
    },
    handleSizeChange(size) {
      this.pageSize = size
      this.fetchBooks()
    },
    handleCurrentChange(page) {
      this.currentPage = page
      this.fetchBooks()
    },
    handleFileChange(file) {
      this.uploadForm.file = file.raw
    },
    async handleUpload() {
      this.$refs.uploadForm.validate(async (valid) => {
        if (!valid || !this.uploadForm.file) return

        this.uploading = true
        try {
          const formData = new FormData()
          formData.append('title', this.uploadForm.title)
          formData.append('author', this.uploadForm.author)
          formData.append('description', this.uploadForm.description)
          formData.append('file', this.uploadForm.file)

          await api.createBook(formData)
          this.$message.success('上传成功')
          this.showUploadDialog = false
          this.resetUploadForm()
          this.fetchBooks()
        } catch (error) {
          console.error('上传失败:', error)
          this.$message.error(error.response?.data?.detail || '上传失败')
        } finally {
          this.uploading = false
        }
      })
    },
    resetUploadForm() {
      this.uploadForm = {
        title: '',
        author: '',
        description: '',
        file: null
      }
      this.$refs.uploadForm.resetFields()
    }
  }
}
</script>

<style scoped>
.books-container {
  min-height: 100vh;
  background-color: #f5f5f5;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100%;
}

.header-left h1 {
  margin: 0;
  font-size: 20px;
  color: #333;
}

.header-right {
  cursor: pointer;
}

.books-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 15px;
  background: white;
  border-radius: 8px;
  gap: 10px;
  flex-wrap: wrap;
}

.books-header h2 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.book-card {
  margin-bottom: 15px;
  cursor: pointer;
  transition: transform 0.3s;
  height: 100%;
}

.book-card:hover {
  transform: translateY(-5px);
}

.book-card:active {
  transform: scale(0.98);
}

.book-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.book-header h3 {
  margin: 0;
  font-size: 15px;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}

.book-content {
  height: 180px;
  overflow: hidden;
}

.book-description {
  font-size: 13px;
  color: #666;
  margin-bottom: 10px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.book-no-description {
  font-size: 13px;
  color: #999;
  margin-bottom: 10px;
}

.book-meta {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: #999;
  margin-bottom: 12px;
  gap: 10px;
}

.reading-progress {
  margin-top: 10px;
}

.progress-text {
  display: block;
  text-align: right;
  font-size: 11px;
  color: #999;
  margin-top: 5px;
}

.loading-container {
  text-align: center;
  padding: 40px;
}

.loading-icon {
  font-size: 32px;
  animation: rotating 2s linear infinite;
}

@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* 移动端适配 */
@media screen and (max-width: 768px) {
  .header-left h1 {
    font-size: 16px;
  }

  .books-header {
    padding: 12px;
  }

  .books-header h2 {
    font-size: 16px;
  }

  .books-header .el-button {
    padding: 8px 12px;
    font-size: 13px;
  }

  .book-content {
    height: 160px;
  }

  .book-header h3 {
    font-size: 14px;
  }

  .book-description,
  .book-no-description {
    font-size: 12px;
  }

  .book-meta {
    font-size: 10px;
  }
}

@media screen and (max-width: 480px) {
  .header-left h1 {
    font-size: 14px;
  }

  .books-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .books-header h2 {
    width: 100%;
    margin-bottom: 10px;
  }

  .books-header .el-button {
    width: 100%;
  }

  .book-content {
    height: 150px;
  }

  .el-col {
    max-width: 50% !important;
    flex: 0 0 50% !important;
  }
}

@media screen and (max-width: 375px) {
  .el-col {
    max-width: 100% !important;
    flex: 0 0 100% !important;
  }
}
</style>
